import {
  onSnapshot,
  QueryDocumentSnapshot,
  DocumentData,
  getDocsFromServer,
  getDocFromServer,
  getDoc,
  Query,
  DocumentReference,
  DocumentSnapshot,
} from 'firebase/firestore'

const noop = () => {}

abstract class BaseService {
  private createDeferred = <T>() => {
    const innerState = {
      resolved: false,
      rejected: false,
    }
    // @ts-expect-error promise is added later
    const deferred: {
      resolve: (value: T) => void
      reject: (error: Error) => void
      promise: Promise<T>
      getIsResolved: () => boolean
      getIsRejected: () => boolean
      getIsComplete: () => boolean
    } = {
      resolve: noop,
      reject: noop,
      getIsResolved: () => {
        return innerState.resolved
      },
      getIsRejected: () => {
        return innerState.rejected
      },
      getIsComplete: () => {
        return innerState.resolved || innerState.rejected
      },
    }
    deferred.promise = new Promise<T>((resolvePromise, rejectPromise) => {
      deferred.resolve = value => {
        innerState.resolved = true
        resolvePromise(value)
      }
      deferred.reject = (error: Error) => {
        innerState.rejected = true
        rejectPromise(error)
      }
    })
    return deferred
  }
  protected onSnapshot = async <T>({
    query,
    onAdded = () => {},
    onUpdated = () => {},
    onRemoved = () => {},
  }: {
    query: Query<T, DocumentData>
    onAdded?: (data: T) => void
    onUpdated?: (data: T) => void
    onRemoved?: (data: T) => void
  }) => {
    const { promise, resolve, reject, getIsComplete } = this.createDeferred<{
      data: T[]
      docs: QueryDocumentSnapshot<T, DocumentData>[]
    }>()
    let skipOnSnapshot = true
    getDocsFromServer(query)
      .then(snapshot =>
        resolve({ docs: snapshot.docs, data: snapshot.docs.map(doc => doc.data()) }),
      )
      .catch(reject)
    const unsubscribe = onSnapshot(
      query,
      async snapshot => {
        if (skipOnSnapshot) {
          skipOnSnapshot = false
          return
        }
        if (!snapshot || !getIsComplete()) {
          return
        }
        snapshot.docChanges().forEach(change => {
          if (change.doc.metadata.hasPendingWrites) {
            return
          }
          if (change.type === 'added' && onAdded) {
            onAdded(change.doc.data())
          }
          if (change.type === 'modified' && onUpdated) {
            onUpdated(change.doc.data())
          }
          if (change.type === 'removed' && onRemoved) {
            onRemoved(change.doc.data())
          }
        })
      },
      () => {
        // unsubscribe on error
        unsubscribe()
      },
    )
    const { docs, data } = await promise
    return {
      docs,
      data,
      unsubscribe,
    }
  }
  protected onDoc = async <T>({
    ref,
    onAdded,
    onUpdated,
    onRemoved,
    forceServerResponse,
  }: {
    ref: DocumentReference<T, DocumentData>
    onAdded?: (data: T) => void
    onUpdated?: (data: T) => void
    onRemoved?: (data: T) => void
    forceServerResponse?: boolean
  }) => {
    let isNewDoc: boolean
    const { promise, resolve, reject, getIsComplete } = this.createDeferred<{
      data: T | null
      doc: DocumentSnapshot<T, DocumentData>
    }>()
    const getDocFunction = forceServerResponse ? getDocFromServer : getDoc
    getDocFunction(ref)
      .then(snapshot => {
        isNewDoc = !snapshot.exists()
        resolve({ doc: snapshot, data: snapshot.data() as T })
      })
      .catch(reject)
    let skipOnSnapshot = true
    const unsubscribe = onSnapshot(
      ref,
      (snapshot: DocumentSnapshot<T, DocumentData>) => {
        if (skipOnSnapshot) {
          skipOnSnapshot = false
          return
        }
        if (!snapshot || !getIsComplete()) {
          return
        }
        if (!snapshot.exists()) {
          if (onRemoved) {
            onRemoved(snapshot.data() as T)
          }
          return
        }
        if (isNewDoc) {
          isNewDoc = false
          if (onAdded) {
            onAdded(snapshot.data())
          }
          return
        } else {
          if (onUpdated) {
            onUpdated(snapshot.data())
          }
          return
        }
      },
      () => {
        // unsubscribe on error
        unsubscribe()
      },
    )
    const { doc, data } = await promise
    return {
      doc,
      data,
      unsubscribe,
    }
  }
}

export default BaseService
